import React, { useEffect, useMemo, useState } from 'react'
import { Form, Message } from 'semantic-ui-react'
import { CustomDropdown } from 'elements'
import { GetTeamMemberList } from 'hooks/user'
import {
    ActionPlanRuleService,
    GroupService,
    PaginationType,
} from 'utility'
import {
    assigneePanelKeyFromType,
    FALLBACK_ASSIGNEE_TYPES,
    mapTeamMemberToPickerUser,
    normalizeActionPlanTeamList,
    normalizeTeamGroupList,
    normalizeVendorCompanyList,
    normalizeVendorContactList,
    pickerItemMatchesId,
    resolveAssigneeTypes,
} from './utils'
import { formatUserPickerLabel } from 'page-components/auto-ticket-rules/utils'

const TYPE_OPTIONS = [
    { key: '1', value: '1', text: 'User' },
    { key: '2', value: '2', text: 'Team' },
    { key: '3', value: '3', text: 'Vendor company' },
    { key: '4', value: '4', text: 'Vendor contact' },
]

const toStringOptions = (items, getText, getDescription) =>
    (items || []).map(item => ({
        key: String(item.id),
        value: String(item.id),
        text: getText ? getText(item) : item.name,
        ...(getDescription
            ? { description: getDescription(item) || '' }
            : {}),
    }))

const withSelectedOption = (
    options,
    items,
    selectedId,
    recipientLabel,
    fallbackLabel,
) => {
    if (!selectedId) return options
    const matched = (items || []).find(item =>
        pickerItemMatchesId(item, selectedId),
    )
    if (matched) {
        return options.map(opt =>
            String(opt.key) === String(matched.id)
                ? { ...opt, key: selectedId, value: selectedId }
                : opt,
        )
    }
    if (options.some(opt => String(opt.value) === String(selectedId))) {
        return options
    }
    return [
        {
            key: String(selectedId),
            value: String(selectedId),
            text:
                recipientLabel || fallbackLabel || `Selected #${selectedId}`,
        },
        ...options,
    ]
}

const panelKeyForTypeId = (typeId, assignedToTypes) => {
    const row = (assignedToTypes || []).find(
        t => Number(t.id) === Number(typeId),
    )
    if (row) return assigneePanelKeyFromType(row)
    if (Number(typeId) === 2) return 'team'
    if (Number(typeId) === 3) return 'vendor-company'
    if (Number(typeId) === 4) return 'vendor-contact'
    return 'user'
}

const parseId = value => {
    if (value == null || value === '') return null
    const numeric = Number(value)
    if (Number.isFinite(numeric) && numeric !== 0) return numeric
    return null
}

export const RecipientPicker = ({
    typeId,
    recipientId,
    vendorCompanyId,
    recipientLabel = '',
    onChange,
    required = true,
    disabled = false,
    label = 'Escalation recipient',
    help,
}) => {
    const { data: teamMembers } = GetTeamMemberList(PaginationType.All)
    const [assignedToTypes, setAssignedToTypes] = useState(
        FALLBACK_ASSIGNEE_TYPES,
    )
    const [teams, setTeams] = useState([])
    const [vendors, setVendors] = useState([])
    const [contacts, setContacts] = useState([])

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            try {
                const [typesRes, teamsRes, groupRes, vendorsRes] =
                    await Promise.all([
                        ActionPlanRuleService.getAssignedToTypes().catch(
                            () => [],
                        ),
                        ActionPlanRuleService.getTeams().catch(() => []),
                        GroupService.list({
                            PageNumber: 1,
                            PageSize: 500,
                        }).catch(() => null),
                        ActionPlanRuleService.getVendorCompanies().catch(
                            () => [],
                        ),
                    ])
                if (cancelled) return
                const types = resolveAssigneeTypes(typesRes)
                setAssignedToTypes(
                    types.length ? types : FALLBACK_ASSIGNEE_TYPES,
                )
                const actionPlanTeams = normalizeActionPlanTeamList(teamsRes)
                const groupTeams =
                    groupRes && !actionPlanTeams.length
                        ? normalizeTeamGroupList(groupRes)
                        : []
                setTeams(actionPlanTeams.length ? actionPlanTeams : groupTeams)
                setVendors(normalizeVendorCompanyList(vendorsRes))
            } catch {
                if (!cancelled) {
                    setAssignedToTypes(FALLBACK_ASSIGNEE_TYPES)
                }
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (Number(typeId) !== 4 || !vendorCompanyId) {
            setContacts([])
            return undefined
        }
        let cancelled = false
        ActionPlanRuleService.getVendorContacts(vendorCompanyId)
            .then(res => {
                if (!cancelled) setContacts(normalizeVendorContactList(res))
            })
            .catch(() => {
                if (!cancelled) setContacts([])
            })
        return () => {
            cancelled = true
        }
    }, [typeId, vendorCompanyId])

    const users = useMemo(
        () =>
            (teamMembers || [])
                .map(mapTeamMemberToPickerUser)
                .filter(
                    u =>
                        u.id != null &&
                        Number.isFinite(Number(u.id)) &&
                        Number(u.id) !== 0,
                ),
        [teamMembers],
    )

    const panelKey = panelKeyForTypeId(typeId, assignedToTypes)
    const selectedId =
        recipientId == null || recipientId === '' ? '' : String(recipientId)
    const selectedVendorId =
        vendorCompanyId == null || vendorCompanyId === ''
            ? ''
            : String(vendorCompanyId)

    const typeOptions = useMemo(() => {
        const fromApi = (assignedToTypes || [])
            .filter(t => {
                const key = assigneePanelKeyFromType(t)
                return [
                    'user',
                    'team',
                    'vendor-company',
                    'vendor-contact',
                ].includes(key)
            })
            .map(t => ({
                key: String(t.id),
                value: String(t.id),
                text: t.name?.replace(/Internal/g, '').trim() || t.name,
            }))
        return fromApi.length ? fromApi : TYPE_OPTIONS
    }, [assignedToTypes])

    const userOptions = useMemo(
        () =>
            withSelectedOption(
                toStringOptions(users, u =>
                    u.email
                        ? `${formatUserPickerLabel(u)} (${u.email})`
                        : formatUserPickerLabel(u),
                ),
                users,
                selectedId,
                recipientLabel,
                'Selected user',
            ),
        [users, selectedId, recipientLabel],
    )
    const teamOptions = useMemo(
        () =>
            withSelectedOption(
                toStringOptions(teams),
                teams,
                selectedId,
                recipientLabel,
                'Selected team',
            ),
        [teams, selectedId, recipientLabel],
    )
    const vendorOptions = useMemo(
        () => toStringOptions(vendors),
        [vendors],
    )
    const contactOptions = useMemo(
        () =>
            withSelectedOption(
                toStringOptions(contacts, c => c.name, c => c.email || ''),
                contacts,
                selectedId,
                recipientLabel,
                'Selected contact',
            ),
        [contacts, selectedId, recipientLabel],
    )

    const emit = patch => {
        const next = {
            typeId: Number(typeId) || 1,
            recipientId: recipientId ?? null,
            vendorCompanyId: vendorCompanyId ?? null,
            ...patch,
        }
        let labelText = ''
        let email = ''
        const nextPanel = panelKeyForTypeId(next.typeId, assignedToTypes)
        if (nextPanel === 'user') {
            const u = users.find(x =>
                pickerItemMatchesId(x, next.recipientId),
            )
            if (u?.email) {
                labelText = `${u.name} (${u.email})`
                email = u.email
            } else if (u?.name) {
                labelText = u.name
            }
        } else if (nextPanel === 'team') {
            labelText =
                teams.find(x => pickerItemMatchesId(x, next.recipientId))
                    ?.name || ''
        } else if (nextPanel === 'vendor-company') {
            labelText =
                vendors.find(x => pickerItemMatchesId(x, next.recipientId))
                    ?.name || ''
        } else {
            const c = contacts.find(x =>
                pickerItemMatchesId(x, next.recipientId),
            )
            labelText = c?.name || ''
            email = c?.email || ''
        }
        onChange?.({ ...next, label: labelText, email })
    }

    return (
        <>
            <Form.Field required={required}>
                <label>{label}</label>
                {help && (
                    <span
                        className="helper"
                        style={{ display: 'block', marginBottom: 8 }}
                    >
                        {help}
                    </span>
                )}
                <CustomDropdown
                    fluid
                    selection
                    selectOnBlur={false}
                    placeholder="Recipient type"
                    options={typeOptions}
                    value={typeId != null ? String(typeId) : ''}
                    disabled={disabled}
                    onChange={(_, { value }) => {
                        const nextType = parseId(value) || 1
                        if (Number(nextType) === Number(typeId)) return
                        emit({
                            typeId: nextType,
                            recipientId: null,
                            vendorCompanyId: null,
                        })
                    }}
                />
            </Form.Field>
            {panelKey === 'user' && (
                <Form.Field required={required}>
                    <label>Choose user</label>
                    <CustomDropdown
                        fluid
                        search
                        selection
                        selectOnBlur={false}
                        placeholder="Select user"
                        options={userOptions}
                        value={selectedId}
                        disabled={disabled || !userOptions.length}
                        onChange={(_, { value }) =>
                            emit({
                                recipientId: parseId(value),
                            })
                        }
                        noResultsMessage="No users found"
                    />
                    {!userOptions.length && (
                        <Message
                            size="small"
                            info
                            content="No team members found. Add users in Settings."
                        />
                    )}
                </Form.Field>
            )}
            {panelKey === 'team' && (
                <Form.Field required={required}>
                    <label>Choose team</label>
                    <CustomDropdown
                        fluid
                        search
                        selection
                        selectOnBlur={false}
                        placeholder="Select team"
                        options={teamOptions}
                        value={selectedId}
                        disabled={disabled || !teamOptions.length}
                        onChange={(_, { value }) =>
                            emit({
                                recipientId: parseId(value),
                            })
                        }
                        noResultsMessage="No teams found"
                    />
                </Form.Field>
            )}
            {panelKey === 'vendor-company' && (
                <Form.Field required={required}>
                    <label>Choose vendor company</label>
                    <CustomDropdown
                        fluid
                        search
                        selection
                        selectOnBlur={false}
                        placeholder="Select vendor company"
                        options={vendorOptions}
                        value={selectedId}
                        disabled={disabled || !vendorOptions.length}
                        onChange={(_, { value }) =>
                            emit({
                                recipientId: parseId(value),
                            })
                        }
                        noResultsMessage="No vendor companies found"
                    />
                </Form.Field>
            )}
            {panelKey === 'vendor-contact' && (
                <>
                    <Form.Field required={required}>
                        <label>Vendor company</label>
                        <CustomDropdown
                            fluid
                            search
                            selection
                            selectOnBlur={false}
                            placeholder="Select vendor company"
                            options={vendorOptions}
                            value={selectedVendorId}
                            disabled={disabled}
                            onChange={(_, { value }) =>
                                emit({
                                    vendorCompanyId: parseId(value),
                                    recipientId: null,
                                })
                            }
                        />
                    </Form.Field>
                    <Form.Field required={required}>
                        <label>Vendor contact</label>
                        <CustomDropdown
                            fluid
                            search
                            selection
                            selectOnBlur={false}
                            placeholder="Select vendor contact"
                            options={contactOptions}
                            value={selectedId}
                            disabled={disabled || !vendorCompanyId}
                            onChange={(_, { value }) =>
                                emit({
                                    recipientId: parseId(value),
                                })
                            }
                            noResultsMessage="No contacts found"
                        />
                    </Form.Field>
                </>
            )}
        </>
    )
}
